<template>
  <div class="asset-visit-form-data">
    <template v-if="variant === 'grid'">
      <key-value-list variant="grid" :size="size">
        <key-value :label="phoneTitle" :value="assetVisit.phone ?? '----'" />
        <key-value
          v-if="showCompanyName && companyName"
          :label="companyTitle"
          :value="companyName" />

        <key-value v-for="data of assetVisitFormData" :key="data.id" :label="data.label">
          <template #value>
            <span
              v-if="isDocument(data)"
              v-html="
                getCustomFieldFormattedValue(data, {
                  [CustomFieldType.Document]: { generateLink: true }
                })
              "></span>
            <span
              v-else-if="isMultiDocument(data)"
              v-html="
                getCustomFieldFormattedValue(data, {
                  [CustomFieldType.MultiDocument]: { generateLink: true }
                })
              "></span>
            <span v-else>{{ getCustomFieldFormattedValue(data) ?? '----' }}</span>
          </template>
        </key-value>

        <key-value
          :label="
            t('assetVisit.dialogs.checkinNotificationPanelDialog.headers.appointmentIdentifier')
          "
          :value="assetVisit.driverAppointmentIdentifier"
          v-if="assetVisit.driverAppointmentIdentifier" />

        <key-value
          :label="t('global.basicTerms.notes', 2)"
          :value="assetVisit.driverNotes"
          v-if="assetVisit.driverNotes" />
      </key-value-list>
    </template>

    <template v-else>
      <key-value-list :is-divided="isDivided" variant="list-stacked" :size="size">
        <div class="d-flex flex-column gap-2">
          <div class="d-flex flex-row justify-space-between align-center">
            <key-value :label="phoneTitle">
              <template #value>
                <span data-testid="asset-visit-form-drivers-phone-value" v-if="!isEditingPhone">
                  {{ assetVisit.phone ?? '----' }}
                </span>
              </template>
            </key-value>
            <div v-if="allowPhoneEdit">
              <v-btn
                icon
                variant="plain"
                size="small"
                @click="isEditingPhone = true"
                data-testid="drivers-chat-edit-phone-btn">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </div>

          <div v-if="allowPhoneEdit && isEditingPhone" class="d-flex flex-column gap-2">
            <phone-number-field
              dense
              v-model="newAssetVisitPhone"
              :validator="$validator"
              outlined
              hide-details
              class="mb-1"
              data-testid="asset-visit-form-drivers-phone"
              :placeholder="
                t('assetVisit.dialogs.checkinNotificationPanelDialog.placeholders.driversPhone')
              "
              :label="null"></phone-number-field>
            <div class="justify-end d-flex flex-row gap-2 pb-3">
              <v-btn size="small" @click="isEditingPhone = false" variant="outlined">
                {{ t('global.buttons.nevermind') }}
              </v-btn>
              <v-btn
                size="small"
                color="primary"
                @click="saveNewAssetVisitPhone"
                data-testid="drivers-chat-save-driver-phone-btn">
                {{ t('global.buttons.save') }}
              </v-btn>
            </div>
          </div>
        </div>

        <key-value label="Carrier Company" :value="companyName" v-if="companyName" />

        <key-value v-for="data of assetVisitFormData" :key="data.id" :label="data.label">
          <template #value>
            <span
              v-if="isDocument(data)"
              v-html="
                getCustomFieldFormattedValue(data, {
                  [CustomFieldType.Document]: { generateLink: true }
                })
              "></span>
            <span
              v-else-if="isMultiDocument(data)"
              v-html="
                getCustomFieldFormattedValue(data, {
                  [CustomFieldType.MultiDocument]: { generateLink: true }
                })
              "></span>
            <span v-else>{{ getCustomFieldFormattedValue(data) ?? '----' }}</span>
          </template>
        </key-value>

        <key-value
          v-if="assetVisit.driverAppointmentIdentifier"
          :label="
            t('assetVisit.dialogs.checkinNotificationPanelDialog.headers.appointmentIdentifier')
          "
          :value="assetVisit.driverAppointmentIdentifier" />

        <key-value
          v-if="assetVisit.driverNotes"
          :label="t('global.basicTerms.notes')"
          :value="assetVisit.driverNotes" />
      </key-value-list>
    </template>
  </div>
</template>
<script>
import { computed, onMounted, ref } from 'vue';
import { CustomFieldType, getCustomFieldFormattedValue } from '@nova/core';
import { useAssetVisit, useEventHub } from '@/composables';
import customFormsService from '@satellite/services/custom-forms-service';
import KeyValueList from '@satellite/components/elements/key_value/KeyValueList.vue';
import KeyValue from '@satellite/components/elements/key_value/KeyValue.vue';
import { useI18n } from 'vue-i18n';
import { camelCase } from 'lodash';

export default {
  components: {
    KeyValueList,
    KeyValue
  },
  props: {
    assetVisit: {
      type: Object,
      required: true
    },
    company: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    /**
     * TODO: ideally we should not need this prop; we could have a store
     * state that holds the selected warehouse (a global one, not a module-based)
     */
    warehouse: {
      type: Object,
      required: false,
      default: null
    },
    phoneTitle: {
      type: String,
      required: false
    },
    companyTitle: {
      type: String,
      required: false
    },
    variant: {
      type: String,
      required: false,
      default: 'list',
      validator(value) {
        return ['list', 'grid'].includes(value);
      }
    },
    size: {
      type: String,
      required: false,
      default: 'regular',
      validator(value) {
        return ['regular', 'small'].includes(value);
      }
    },
    isDivided: {
      type: Boolean,
      required: false,
      default: false
    },
    allowPhoneEdit: {
      type: Boolean,
      required: false,
      default: false
    },
    showCompanyName: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  setup(props) {
    const { t } = useI18n();
    const assetVisitFormData = ref([]);
    const isEditingPhone = ref(false);
    const newAssetVisitPhone = ref(null);
    const { updateAssetVisit } = useAssetVisit(props.assetVisit);

    const translatedPhoneTitle = computed(() =>
      t('assetVisit.dialogs.checkinNotificationPanelDialog.headers.phoneNumber')
    );
    const translatedCompanyTitle = computed(() =>
      t('assetVisit.dialogs.checkinNotificationPanelDialog.headers.carrierCompany')
    );

    const eventHub = useEventHub();

    const companyName = computed(() => {
      return props.company?.name ?? props.assetVisit.companyHint;
    });

    async function getAssetVisitFormData() {
      try {
        const fields = await customFormsService.getCustomFormData({
          s: { objectId: props.assetVisit.id },
          limit: 1000
        });
        return fields.map(field => {
          field.formField.field = {
            ...field.formField.field,
            label: t(
              `core.customForms.customFormsFeaturesData.labels.${camelCase(field.label)}`,
              field.label
            )
          };
          return {
            ...field,
            label: t(
              `core.customForms.customFormsFeaturesData.labels.${camelCase(field.label)}`,
              field.label
            )
          };
        });
      } catch (_) {
        return [];
      }
    }

    function isDocument(field) {
      return field.type === CustomFieldType.Document;
    }

    function isMultiDocument(field) {
      return field.type === CustomFieldType.MultiDocument;
    }

    const saveNewAssetVisitPhone = async () => {
      await updateAssetVisit(props.assetVisit.id, {
        phone: newAssetVisitPhone.value
      });
      isEditingPhone.value = false;
      eventHub.$emit('refresh-AssetVisit', props.assetVisit);
    };

    onMounted(async () => {
      assetVisitFormData.value = await getAssetVisitFormData();
    });

    return {
      assetVisitFormData,
      isDocument,
      isMultiDocument,
      CustomFieldType,
      getCustomFieldFormattedValue,
      isEditingPhone,
      saveNewAssetVisitPhone,
      newAssetVisitPhone,
      companyName,
      translatedPhoneTitle,
      translatedCompanyTitle,
      t
    };
  }
};
</script>
